import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectUserID, selectFullName } from "../../../redux/IchthusSlice";
import { domain } from "../../../security";
import {
  FaTimes,
  FaTruck,
  FaInfoCircle,
  FaCheckCircle,
  FaTrash,
  FaCheckDouble,
  FaQrcode,
  FaExclamationTriangle,
  FaWarehouse,
  FaSearch,
} from "react-icons/fa";
import { Loader2, ScanLine, Package, ArrowRight, X } from "lucide-react";

// ─── SERIAL PICKER MODAL (inline, for warehouse serials) ──────────────────────
const SerialPickerModal = ({
  line,
  sourceLocationId,
  sourceWarehouseId,
  excludedIds,
  onSave,
  onClose,
}) => {
  const [availableSerials, setAvailableSerials] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const needed = line.quantityToReceive;

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        let url = `${domain}/api/SerialNumbers/available?productId=${line.productId}`;
        if (sourceLocationId) url += `&locationId=${sourceLocationId}`;
        const res = await axios.get(url);
        const filtered = (res.data || []).filter(
          (s) => !excludedIds.includes(s.id),
        );
        setAvailableSerials(filtered);
      } catch {
        toast.error("Failed to load serials.");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [line.productId, sourceLocationId]);

  const filtered = search
    ? availableSerials.filter((s) =>
        s.serialName?.toLowerCase().includes(search.toLowerCase()),
      )
    : availableSerials;

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length < needed
          ? [...prev, id]
          : prev,
    );
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-bold text-slate-800">{line.productName}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Select <span className="font-bold text-indigo-600">{needed}</span>{" "}
              serial(s) — {selected.length} chosen
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-slate-100">
          <div className="relative">
            <FaSearch
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={12}
            />
            <input
              type="text"
              placeholder="Search serial number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 size={24} className="animate-spin text-indigo-500" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-slate-400 py-8 text-sm">
              No available serials found.
            </p>
          ) : (
            <div className="space-y-1.5">
              {filtered.map((s) => {
                const isSelected = selected.includes(s.id);
                const isDisabled = !isSelected && selected.length >= needed;
                return (
                  <button
                    key={s.id}
                    onClick={() => toggle(s.id)}
                    disabled={isDisabled}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-all text-sm font-mono ${
                      isSelected
                        ? "bg-indigo-50 border-indigo-300 text-indigo-800"
                        : isDisabled
                          ? "bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed"
                          : "bg-white border-slate-200 text-slate-700 hover:border-indigo-200 hover:bg-indigo-50/40"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded flex items-center justify-center border-2 shrink-0 transition-all ${
                        isSelected
                          ? "bg-indigo-600 border-indigo-600"
                          : "border-slate-300"
                      }`}
                    >
                      {isSelected && (
                        <FaCheckCircle size={10} className="text-white" />
                      )}
                    </div>
                    {s.serialName}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
          <span className="text-xs text-slate-500 font-semibold">
            {selected.length}/{needed} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 font-medium transition"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onSave(selected);
                onClose();
              }}
              disabled={selected.length !== needed}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-40 transition"
            >
              Confirm ({selected.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const AddWarehouseReceiving = ({ onClose, onSuccess }) => {
  const currentUserId = useSelector(selectUserID);
  const currentUserName = useSelector(selectFullName);

  // ── QR / WR selection state ──
  const [scanInput, setScanInput] = useState("");
  const [approvedWRs, setApprovedWRs] = useState([]);
  const [selectedWRId, setSelectedWRId] = useState("");
  const scanRef = useRef(null);

  // ── WR data ──
  const [wrDetails, setWrDetails] = useState(null);
  const [lineProgress, setLineProgress] = useState([]);
  const [receivingHistory, setReceivingHistory] = useState([]);
  const [isLoadingWR, setIsLoadingWR] = useState(false);

  // ── Form ──
  const [deliveryLines, setDeliveryLines] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Serial Picker ──
  const [activeSerial, setActiveSerial] = useState(null); // { lineIndex, ...line }

  // ── Fetch approved WRs for dropdown ──
  useEffect(() => {
    axios
      .get(`${domain}/api/WarehouseRequests`)
      .then((res) => {
        const approved = (res.data || []).filter(
          (w) => w.status === "Approved" || w.status === "Partially Received",
        );
        setApprovedWRs(approved);
      })
      .catch(() => toast.error("Failed to load Warehouse Requests."));
  }, []);

  // ── Load WR data when selected ──
  const loadWR = useCallback(async (wrId) => {
    if (!wrId) return;
    setIsLoadingWR(true);
    try {
      const res = await axios.get(
        `${domain}/api/WarehouseReceiving/byRequest/${wrId}`,
      );
      const {
        warehouseRequest,
        lineProgress: lp,
        receivingHistory: rh,
      } = res.data;

      setWrDetails(warehouseRequest);
      setLineProgress(lp);
      setReceivingHistory(rh);

      // Initialize delivery lines
      setDeliveryLines(
        lp.map((line) => ({
          ...line,
          quantityToReceive: line.canReceive > 0 ? line.canReceive : 0,
          serialNumbers: [],
        })),
      );
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to load Warehouse Request.",
      );
      setWrDetails(null);
      setLineProgress([]);
      setDeliveryLines([]);
    } finally {
      setIsLoadingWR(false);
    }
  }, []);

  useEffect(() => {
    if (selectedWRId) loadWR(selectedWRId);
  }, [selectedWRId, loadWR]);

  // ── QR SCAN: fires on Enter key ──
  const handleScanKeyDown = async (e) => {
    if (e.key !== "Enter" || !scanInput.trim()) return;
    const query = scanInput.trim();
    setScanInput("");

    // Try to match directly against WR number in the list
    const match = approvedWRs.find(
      (w) => w.requestNumber?.toLowerCase() === query.toLowerCase(),
    );
    if (match) {
      setSelectedWRId(String(match.id));
      return;
    }

    // Or call the scan endpoint
    try {
      const res = await axios.get(
        `${domain}/api/WarehouseReceiving/scan/${encodeURIComponent(query)}`,
      );
      if (res.data?.warehouseRequestId) {
        setSelectedWRId(String(res.data.warehouseRequestId));
        toast.success(`Loaded: ${query}`);
      }
    } catch {
      toast.error(`No active WR found for: ${query}`);
    }
  };

  // ── Line qty change ──
  const handleQtyChange = (index, value) => {
    const lines = [...deliveryLines];
    const line = lines[index];
    let newQty = parseFloat(value) || 0;
    if (newQty < 0) newQty = 0;
    if (newQty > line.canReceive) {
      toast.warn(`Max receivable: ${line.canReceive} for ${line.productName}`);
      newQty = line.canReceive;
    }
    lines[index] = { ...line, quantityToReceive: newQty, serialNumbers: [] };
    setDeliveryLines(lines);
  };

  const handleClearLine = (index) => {
    const lines = [...deliveryLines];
    lines[index] = { ...lines[index], quantityToReceive: 0, serialNumbers: [] };
    setDeliveryLines(lines);
  };

  const handleSetMaxAll = () => {
    setDeliveryLines((prev) =>
      prev.map((l) => ({
        ...l,
        quantityToReceive: l.canReceive,
        serialNumbers: [],
      })),
    );
  };

  const handleSaveSerials = (lineIndex, serialIds) => {
    const lines = [...deliveryLines];
    lines[lineIndex] = { ...lines[lineIndex], serialNumbers: serialIds };
    setDeliveryLines(lines);
    setActiveSerial(null);
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!selectedWRId) return toast.error("Select a Warehouse Request.");

    const validLines = deliveryLines.filter((l) => l.quantityToReceive > 0);
    if (validLines.length === 0)
      return toast.warn("Enter a quantity for at least one item.");

    const invalidSerials = validLines.filter(
      (l) => l.hasSerial && l.serialNumbers.length !== l.quantityToReceive,
    );
    if (invalidSerials.length > 0) {
      return toast.error(
        `Pick exactly ${invalidSerials[0].quantityToReceive} serial(s) for "${invalidSerials[0].productName}".`,
      );
    }

    setIsSubmitting(true);
    try {
      const payload = {
        warehouseRequestId: parseInt(selectedWRId),
        receivedDate: new Date().toISOString(),
        receivedByUserId: currentUserId,
        remarks,
        items: validLines.map((l) => ({
          warehouseRequestItemId: l.id,
          quantityReceived: l.quantityToReceive,
          serialNumberIds: l.hasSerial ? l.serialNumbers : [],
        })),
      };

      const res = await axios.post(`${domain}/api/WarehouseReceiving`, payload);
      toast.success(res.data?.message || "Receiving created!");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create receiving.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Derived ──
  const allExcludedSerials = deliveryLines.flatMap((l) => l.serialNumbers);
  const totalToReceive = deliveryLines.reduce(
    (s, l) => s + (l.quantityToReceive || 0),
    0,
  );
  const stockShortageLines = deliveryLines.filter(
    (l) => l.remainingQuantity > 0 && l.availableStock === 0,
  );

  const isFromLocation = wrDetails?.originLocationId > 0;
  const sourceLocationId = isFromLocation ? wrDetails?.originLocationId : null;
  const sourceWarehouseId = !isFromLocation
    ? wrDetails?.originWarehouseId
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 p-4 backdrop-blur-sm">
      {/* Serial picker */}
      {activeSerial && (
        <SerialPickerModal
          line={activeSerial}
          sourceLocationId={sourceLocationId}
          sourceWarehouseId={sourceWarehouseId}
          excludedIds={allExcludedSerials}
          onSave={(ids) => handleSaveSerials(activeSerial.lineIndex, ids)}
          onClose={() => setActiveSerial(null)}
        />
      )}

      <div className="relative flex h-full max-h-[95vh] w-full max-w-6xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* ── HEADER ── */}
        <div className="flex shrink-0 items-center justify-between border-b bg-indigo-50 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600 rounded-xl text-white shadow-lg shadow-indigo-200">
              <FaWarehouse size={20} />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-800">
                New Warehouse Receiving
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Scan or select an Approved Warehouse Request to receive stock
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {isSubmitting && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/60">
            <Loader2 size={36} className="animate-spin text-indigo-600" />
          </div>
        )}

        {/* ── BODY ── */}
        <div className="flex-grow overflow-y-auto p-6 space-y-5 bg-white">
          {/* SECTION 1: WR Selection */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* QR Scanner */}
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-1.5">
                <ScanLine size={12} className="inline mr-1" />
                Scan QR Code (WR Number)
              </label>
              <div className="relative">
                <FaQrcode
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-indigo-400"
                  size={16}
                />
                <input
                  ref={scanRef}
                  type="text"
                  value={scanInput}
                  onChange={(e) => setScanInput(e.target.value)}
                  onKeyDown={handleScanKeyDown}
                  placeholder="Focus here and scan QR… or type WR number + Enter"
                  className="w-full pl-10 pr-4 py-2.5 text-sm font-mono border-2 border-indigo-200 rounded-xl bg-indigo-50 focus:outline-none focus:border-indigo-500 focus:bg-white transition-all placeholder-indigo-300"
                  autoFocus
                />
              </div>
              <p className="text-[10px] text-slate-400 mt-1 font-medium">
                Point your QR scanner at the printed WR slip and press the scan
                button.
              </p>
            </div>

            {/* OR dropdown */}
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-1.5">
                Or Select from Approved Requests
              </label>
              <select
                value={selectedWRId}
                onChange={(e) => setSelectedWRId(e.target.value)}
                className="w-full px-3 py-2.5 text-sm font-semibold border border-slate-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 cursor-pointer"
              >
                <option value="">— Select Warehouse Request —</option>
                {approvedWRs.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.requestNumber} · {w.warehouseName} · {w.status}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isLoadingWR && (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={28} className="animate-spin text-indigo-500" />
            </div>
          )}

          {/* SECTION 2: WR Info Banner */}
          {wrDetails && !isLoadingWR && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <FaInfoCircle
                className="text-blue-500 mt-0.5 shrink-0"
                size={16}
              />
              <div className="flex-1">
                <h4 className="font-bold text-blue-900 text-sm">
                  Warehouse Request Details
                </h4>
                <div className="flex flex-wrap gap-4 mt-1">
                  <p className="text-xs text-blue-700">
                    WR#:{" "}
                    <span className="font-black">
                      {
                        approvedWRs.find(
                          (w) => String(w.id) === String(selectedWRId),
                        )?.requestNumber
                      }
                    </span>
                  </p>
                  <p className="text-xs text-blue-700">
                    Destination:{" "}
                    <span className="font-bold">
                      {
                        approvedWRs.find(
                          (w) => String(w.id) === String(selectedWRId),
                        )?.warehouseName
                      }
                    </span>
                  </p>
                  <p className="text-xs text-blue-700">
                    Status:{" "}
                    <span className="font-bold">{wrDetails.status}</span>
                  </p>
                  {isFromLocation && (
                    <p className="text-xs text-blue-700">
                      Source:{" "}
                      <span className="font-bold">
                        📍 Location #{wrDetails.originLocationId}
                      </span>
                    </p>
                  )}
                  {sourceWarehouseId && (
                    <p className="text-xs text-blue-700">
                      Source:{" "}
                      <span className="font-bold">
                        🏭 Warehouse #{sourceWarehouseId}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Stock shortage banner */}
          {stockShortageLines.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
              <FaExclamationTriangle
                className="text-red-500 shrink-0"
                size={14}
              />
              <p className="text-xs text-red-700 font-medium">
                <span className="font-black">
                  {stockShortageLines.length} item(s)
                </span>{" "}
                have no available stock at the source and cannot be received
                yet.
              </p>
            </div>
          )}

          {/* SECTION 3: Receiving History (collapsible) */}
          {receivingHistory.length > 0 && (
            <details className="rounded-xl border border-slate-200 overflow-hidden">
              <summary className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer text-sm font-bold text-slate-700 select-none">
                <span>📋 Previous Receivings ({receivingHistory.length})</span>
                <span className="text-slate-400 text-xs font-normal">
                  click to expand
                </span>
              </summary>
              <div className="divide-y divide-slate-100">
                {receivingHistory.map((rv) => (
                  <div
                    key={rv.id}
                    className={`px-4 py-3 ${rv.isVoid ? "opacity-50" : ""}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-sm text-indigo-700">
                        {rv.receivingNumber}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">
                          {rv.receivedDate
                            ? new Date(rv.receivedDate).toLocaleDateString()
                            : "—"}
                        </span>
                        <span
                          className={`text-[10px] font-black px-2 py-0.5 rounded-full ${rv.isVoid ? "bg-red-100 text-red-600" : "bg-green-100 text-green-700"}`}
                        >
                          {rv.isVoid ? "VOIDED" : rv.status}
                        </span>
                      </div>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {rv.items?.map((item, i) => (
                        <span
                          key={i}
                          className="text-[11px] bg-white border border-slate-200 px-2 py-0.5 rounded font-medium text-slate-600"
                        >
                          {item.productName}:{" "}
                          <strong>{item.quantityReceived}</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* SECTION 4: Line Items Table */}
          {wrDetails && !isLoadingWR && deliveryLines.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-black text-slate-700">
                  Receiving Lines ({deliveryLines.length} items)
                </h3>
                <button
                  type="button"
                  onClick={handleSetMaxAll}
                  className="text-xs font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-lg hover:bg-indigo-100 transition"
                >
                  Receive All Available
                </button>
              </div>

              <div className="rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="max-h-[420px] overflow-y-auto">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-black text-slate-500 uppercase w-[30%]">
                          Product
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-black text-slate-500 uppercase w-20">
                          Requested
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-black text-orange-600 uppercase w-20">
                          Remaining
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-black text-emerald-700 uppercase w-24">
                          At Source
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-black text-indigo-700 uppercase w-28">
                          Receive Qty
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-black text-slate-500 uppercase w-40">
                          Serials
                        </th>
                        <th className="px-3 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {deliveryLines.map((line, index) => {
                        const isFullyReceived = line.remainingQuantity <= 0;
                        const isOutOfStock =
                          line.remainingQuantity > 0 &&
                          line.availableStock === 0;
                        const isLowStock =
                          line.availableStock < line.remainingQuantity &&
                          line.availableStock > 0;
                        const pickedCount = line.serialNumbers.length;
                        const isFullyPicked =
                          line.hasSerial &&
                          pickedCount === line.quantityToReceive &&
                          line.quantityToReceive > 0;

                        return (
                          <tr
                            key={index}
                            className={
                              isFullyReceived
                                ? "bg-slate-50 opacity-50 pointer-events-none"
                                : isOutOfStock
                                  ? "bg-red-50/40 opacity-60 pointer-events-none"
                                  : line.quantityToReceive > 0
                                    ? "bg-indigo-50/30"
                                    : "hover:bg-slate-50/50"
                            }
                          >
                            {/* Product */}
                            <td className="px-5 py-3">
                              <p className="font-bold text-slate-800 text-sm">
                                {line.productName}
                              </p>
                              {line.productCode && (
                                <p className="text-[10px] font-mono text-slate-400 mt-0.5">
                                  {line.productCode}
                                </p>
                              )}
                              {isFullyReceived && (
                                <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 flex items-center gap-1 w-fit mt-1">
                                  <FaCheckDouble size={8} /> Fully Received
                                </span>
                              )}
                              {isOutOfStock && (
                                <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-200 flex items-center gap-1 w-fit mt-1">
                                  <FaExclamationTriangle size={8} /> No Stock
                                </span>
                              )}
                              {isLowStock && (
                                <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 flex items-center gap-1 w-fit mt-1">
                                  <FaWarehouse size={8} /> Low Stock
                                </span>
                              )}
                            </td>

                            {/* Requested */}
                            <td className="px-4 py-3 text-center">
                              <span className="font-bold text-slate-700 text-sm">
                                {line.requestedQuantity}
                              </span>
                            </td>

                            {/* Remaining */}
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`font-bold text-sm ${isFullyReceived ? "text-slate-300" : "text-orange-600"}`}
                              >
                                {line.remainingQuantity}
                              </span>
                            </td>

                            {/* Available at Source */}
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`inline-flex items-center gap-1 font-bold text-sm px-2 py-0.5 rounded-full ${
                                  isOutOfStock
                                    ? "text-red-600 bg-red-100"
                                    : isLowStock
                                      ? "text-amber-700 bg-amber-100"
                                      : "text-emerald-700 bg-emerald-100"
                                }`}
                              >
                                <FaWarehouse size={8} /> {line.availableStock}
                              </span>
                            </td>

                            {/* Qty to Receive */}
                            <td className="px-4 py-3 text-center">
                              <input
                                type="number"
                                min="0"
                                max={line.canReceive}
                                step="1"
                                disabled={isFullyReceived || isOutOfStock}
                                value={line.quantityToReceive}
                                onChange={(e) =>
                                  handleQtyChange(index, e.target.value)
                                }
                                className={`w-20 text-center rounded-lg border py-1.5 font-bold text-sm focus:ring-2 focus:ring-indigo-300 focus:outline-none transition ${
                                  isFullyReceived || isOutOfStock
                                    ? "bg-slate-100 cursor-not-allowed text-slate-400"
                                    : line.quantityToReceive > 0
                                      ? "border-indigo-400 text-indigo-700"
                                      : "border-slate-300 text-slate-700"
                                }`}
                              />
                            </td>

                            {/* Serial Picker */}
                            <td className="px-4 py-3 text-center">
                              {line.hasSerial ? (
                                line.quantityToReceive > 0 ? (
                                  <button
                                    onClick={() =>
                                      setActiveSerial({
                                        ...line,
                                        lineIndex: index,
                                        quantityToReceive:
                                          line.quantityToReceive,
                                      })
                                    }
                                    className={`flex w-full items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] uppercase font-bold transition-all ${
                                      isFullyPicked
                                        ? "bg-green-100 text-green-700 border border-green-200"
                                        : "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 shadow-sm"
                                    }`}
                                  >
                                    {isFullyPicked ? (
                                      <>
                                        <FaCheckCircle size={10} /> Picked (
                                        {pickedCount})
                                      </>
                                    ) : (
                                      <>
                                        <FaQrcode size={10} /> Pick (
                                        {pickedCount}/{line.quantityToReceive})
                                      </>
                                    )}
                                  </button>
                                ) : (
                                  <span className="text-xs text-slate-400 italic">
                                    Set qty first
                                  </span>
                                )
                              ) : (
                                <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded font-medium">
                                  Auto
                                </span>
                              )}
                            </td>

                            {/* Clear */}
                            <td className="px-3 py-3 text-center">
                              {line.quantityToReceive > 0 &&
                                !isFullyReceived && (
                                  <button
                                    onClick={() => handleClearLine(index)}
                                    className="text-red-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded-lg transition"
                                    title="Clear"
                                  >
                                    <FaTrash size={12} />
                                  </button>
                                )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* SECTION 5: Remarks */}
          {wrDetails && !isLoadingWR && (
            <div>
              <label className="block text-xs font-black uppercase tracking-widest text-slate-500 mb-1.5">
                Remarks (optional)
              </label>
              <textarea
                rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add receiving notes..."
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>
          )}

          {/* SECTION 6: Summary */}
          {totalToReceive > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FaCheckCircle className="text-green-500 shrink-0" size={16} />
                <p className="text-sm text-slate-700">
                  Receiving{" "}
                  <span className="font-black text-indigo-700">
                    {totalToReceive} unit(s)
                  </span>{" "}
                  across{" "}
                  <span className="font-black">
                    {
                      deliveryLines.filter((l) => l.quantityToReceive > 0)
                        .length
                    }{" "}
                    line(s)
                  </span>
                </p>
              </div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                Ready to Submit
              </span>
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="shrink-0 border-t bg-slate-50 px-6 py-4 flex justify-between items-center">
          <p className="text-xs text-slate-400 italic">
            {deliveryLines.some(
              (l) =>
                l.quantityToReceive > 0 && l.quantityToReceive < l.canReceive,
            )
              ? "⚠ Partial receiving — remaining can be received in a new session."
              : ""}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 bg-white border border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition shadow-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !wrDetails || totalToReceive === 0}
              className="px-6 py-2.5 bg-indigo-600 text-white font-black rounded-xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-200 disabled:opacity-40 flex items-center gap-2"
            >
              {isSubmitting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <FaWarehouse size={14} />
              )}
              Confirm Receiving
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddWarehouseReceiving;
